// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getBoldedText from "@insite/client-framework/Common/Utilities/getPatternBolded";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
} from "@insite/client-framework/HandlerCreator";
import { GetProductCollectionApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { ProductFilters } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import { ProductAutocompleteItemModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    queryString: string;
    categoryId?: string;
    onSuccess?: (result?: ProductAutocompleteItemModel[]) => void;
}

export interface DisplayProductsResult {
    products?: ProductModel[];
    productFilters: ProductFilters;
}

interface Props {
    apiParameter: GetProductCollectionApiV2Parameter;
    result: DisplayProductsResult;
}

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        categoryId: props.parameter.categoryId,
        expand: [],
    };
};

export const DispatchLoadProducts: HandlerType = async props => {
    const state = props.getState();
    props.result = {
        ...props.result,
        products: await executeAwaitableHandlerChain(
            loadProducts, { ...props.apiParameter }, props,
        )
    }
};

/**
 * prepareProducts maps the ProductModel to ProductAutocompleteItemModel
 * @param products 
 * @param query 
 * @returns array of products of ProductAutocompleteItemModel type used in AutocompleteProducts
 */
const prepareProducts = (products: ProductModel[], query: string): ProductAutocompleteItemModel[] => {
    return products?.map(p => ({
        brandDetailPagePath: p.brand?.detailPagePath || "",
        brandName: p.brand?.name || "",
        erpNumber: p.productNumber,
        id: p.id,
        image: p.smallImagePath || p.mediumImagePath || p.largeImagePath,
        manufacturerItemNumber: p.manufacturerItem,
        name: p.productTitle,
        title: p.productTitle,
        url: p.canonicalUrl,
        isNameCustomerOverride: false,
        styleParentId: null,
        subtitle: "",
        displayErpNumber: getBoldedText(p.productNumber, query),
        displayTitle: getBoldedText(p.productTitle, query),
        displayName: getBoldedText(p.productTitle, query),
        displayManufacturerItemNumber: getBoldedText(
            p.manufacturerItem,
            query,
        ),
        displaySubtitle: getBoldedText(p.productTitle, query),
        uri: p.canonicalUrl,
        properties: p.properties,
    }));
};

/**
 * XngExecuteOnSuccessCallBack calling onSuccess with products
 * @param props 
 */
export const ExecuteOnSuccessCallBack: HandlerType = props => {
    const products = prepareProducts(props.result.products || [], props.parameter.queryString);
    props.parameter.onSuccess?.(products);
};

export const chain = [
    PopulateApiParameter,
    DispatchLoadProducts,
    ExecuteOnSuccessCallBack,
];

const displaySearchProducts = createHandlerChainRunner(chain, "DisplaySearchProducts");
export default displaySearchProducts;
